/**
 * When final pay is due when somebody's employment ends, by state.
 *
 * Researched state by state, 2026-10-10 (docs/notes/legal-deadlines.md has the
 * method and what is left to check). Pure data and one function, no imports:
 * the table is read by core, printed into the notes by a script, and is meant
 * to be reviewed by somebody who knows employment law before anyone relies on
 * it. **It is guidance for the person doing the work, not legal advice**, and
 * every card that quotes it says so.
 *
 * What it changes in the product, and what it deliberately does not:
 * - It tells HR the rule on the final-pay card, with the citation.
 * - Where the law is "on the day" (`atMeeting`), an involuntary termination
 *   gets a card for having the check ready *before* the meeting.
 * - It does **not** move a deadline. The templates already put final payroll on
 *   the last day, which is never later than any state's rule; a per-state date
 *   would only loosen it, and a rule that is wrong in the loose direction is
 *   the harmful one.
 *
 * `source` says how sure each row is:
 * - `statute`: read in the statute's official text;
 * - `agency`: read in the state agency's guidance or a summary that quotes the
 *   statute;
 * - `compilers`: three independent compilations agree and nothing contradicts
 *   them, but the statute text was not read. Verify before relying on it.
 */

export type RuleSource = "statute" | "agency" | "compilers";

export interface FinalPayRule {
  /** Involuntary separation (discharge, termination): when final wages are due. */
  discharge: string;
  /** Voluntary separation (resignation, retirement). */
  quit: string;
  /** A layoff, where the state treats it differently from a discharge. */
  layoff?: string;
  /**
   * The state requires payment the day of discharge or at once, so the check
   * has to exist when the person is told. Only where the statute says so.
   */
  atMeeting: boolean;
  cite: string;
  source: RuleSource;
  /** What late payment costs, only where read in the source. */
  penalty?: string;
  note?: string;
}

const NONE = "No state law; federal law leaves it to the next regular payday.";

/** Keyed by USPS code, plus DC. */
export const FINAL_PAY: Readonly<Record<string, FinalPayRule>> = {
  AL: { discharge: NONE, quit: NONE, atMeeting: false, cite: "none", source: "compilers" },
  AK: {
    discharge: "Within 3 working days.",
    quit: "By the next regular payday that is at least 3 days after notice.",
    atMeeting: false,
    cite: "AS 23.05.140",
    source: "agency",
    penalty: "The employee's regular wage continues from the demand until paid, up to 90 working days.",
  },
  AZ: {
    discharge: "Within 7 working days or the next regular payday, whichever is sooner.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "A.R.S. 23-353",
    source: "compilers",
  },
  AR: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "Ark. Code 11-4-405",
    source: "agency",
    penalty: "Double wages if still unpaid 7 days after that payday and demanded.",
  },
  CA: {
    discharge: "Immediately, at the time of discharge, including earned unused vacation.",
    quit: "Within 72 hours, or on the last day if the employee gave at least 72 hours' notice.",
    atMeeting: true,
    cite: "Cal. Labor Code 201, 202",
    source: "agency",
    penalty: "Waiting-time penalty: the daily wage for each day late, up to 30 days (Labor Code 203).",
    note: "Accrued vacation is wages and is paid out.",
  },
  CO: {
    discharge:
      "Immediately. If payroll is not working at that time, within 6 hours of its next workday start (24 hours if payroll is off site).",
    quit: "By the next regular payday.",
    atMeeting: true,
    cite: "C.R.S. 8-4-109",
    source: "agency",
  },
  CT: {
    discharge: "By the business day after discharge.",
    quit: "By the next regular payday.",
    layoff: "By the next regular payday.",
    atMeeting: false,
    cite: "Conn. Gen. Stat. 31-71c",
    source: "statute",
  },
  DE: {
    discharge: "The later of the next regular payday and 3 business days after the last day worked.",
    quit: "The later of the next regular payday and 3 business days after the last day worked.",
    atMeeting: false,
    cite: "19 Del. C. 1103",
    source: "agency",
    note: "Recently amended; older summaries say only the next regular payday.",
  },
  DC: {
    discharge: "By the working day after discharge (4 days if the employee handled the employer's money).",
    quit: "By the next regular payday or within 7 days, whichever is earlier.",
    layoff: "By the next regular payday.",
    atMeeting: false,
    cite: "D.C. Code 32-1303",
    source: "statute",
  },
  FL: { discharge: NONE, quit: NONE, atMeeting: false, cite: "none", source: "compilers" },
  GA: { discharge: NONE, quit: NONE, atMeeting: false, cite: "none", source: "compilers" },
  HI: {
    discharge: "At the time of discharge, or by the next working day if payment then is impossible.",
    quit: "By the next regular payday, or on the last day if one pay period's notice was given.",
    layoff: "By the next regular payday.",
    atMeeting: true,
    cite: "HRS 388-3",
    source: "agency",
  },
  ID: {
    discharge: "The earlier of the next regular payday and 10 days (weekends and holidays excluded).",
    quit: "The earlier of the next regular payday and 10 days (weekends and holidays excluded).",
    atMeeting: false,
    cite: "Idaho Code 45-606",
    source: "statute",
    note: "Within 48 hours of a written request for earlier payment.",
  },
  IL: {
    discharge: "At the time of separation if possible, and no later than the next regular payday.",
    quit: "At the time of separation if possible, and no later than the next regular payday.",
    atMeeting: false,
    cite: "820 ILCS 115/5",
    source: "compilers",
    note: "Earned unused vacation is paid out.",
  },
  IN: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "IC 22-2-5-1",
    source: "compilers",
  },
  IA: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "Iowa Code 91A.4",
    source: "compilers",
  },
  KS: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "K.S.A. 44-315",
    source: "compilers",
  },
  KY: {
    discharge: "The later of the next regular payday and 14 days.",
    quit: "The later of the next regular payday and 14 days.",
    atMeeting: false,
    cite: "KRS 337.055",
    source: "compilers",
  },
  LA: {
    discharge: "The earlier of the next regular payday and 15 days.",
    quit: "The earlier of the next regular payday and 15 days.",
    atMeeting: false,
    cite: "La. R.S. 23:631",
    source: "compilers",
    note: "Earned vacation is paid out.",
  },
  ME: {
    discharge: "By the next established payday (no more than 2 weeks after a demand).",
    quit: "By the next established payday (no more than 2 weeks after a demand).",
    atMeeting: false,
    cite: "26 M.R.S. 626",
    source: "statute",
    note: "Unused vacation accrued since 2023 is paid out for employers with more than 10 employees.",
  },
  MD: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "Md. Lab. & Empl. 3-505",
    source: "compilers",
  },
  MA: {
    discharge: "In full on the day of discharge.",
    quit: "By the next regular payday (or the following Saturday if there is none).",
    atMeeting: true,
    cite: "M.G.L. c.149 s.148",
    source: "statute",
    penalty: "Mandatory treble damages and attorney's fees (c.149 s.150).",
    note: "Holiday and vacation pay due under an agreement counts as wages.",
  },
  MI: {
    discharge: "Immediately, as soon as the amount can with due diligence be determined.",
    quit: "As soon as the amount can with due diligence be determined.",
    atMeeting: false,
    cite: "MCL 408.475",
    source: "statute",
  },
  MN: {
    discharge:
      "Immediately upon the employee's demand; the employer is in default 24 hours after a written demand.",
    quit: "By the first regular payday after the last day (the second payday if the first is within 5 days, never beyond 20 days).",
    atMeeting: false,
    cite: "Minn. Stat. 181.13, 181.14",
    source: "statute",
  },
  MS: { discharge: NONE, quit: NONE, atMeeting: false, cite: "none", source: "compilers" },
  MO: {
    discharge: "On the day of discharge.",
    quit: "No state law; federal law leaves it to the next regular payday.",
    atMeeting: true,
    cite: "RSMo 290.110",
    source: "statute",
    penalty: "Wages continue at the same rate until paid, up to 60 days, if unpaid 7 days after a request.",
  },
  MT: {
    discharge: "Immediately: within 4 hours or by the end of the workday, whichever is first.",
    quit: "By the next regular payday or 15 days, whichever is first.",
    atMeeting: true,
    cite: "MCA 39-3-205",
    source: "agency",
    note: "A written policy may extend discharge pay only to the next payday or 15 days, whichever is first.",
  },
  NE: {
    discharge: "The earlier of the next regular payday and 2 weeks.",
    quit: "The earlier of the next regular payday and 2 weeks.",
    atMeeting: false,
    cite: "Neb. Rev. Stat. 48-1230",
    source: "compilers",
  },
  NV: {
    discharge: "Immediately.",
    quit: "By the day the employee would regularly be paid, or within 7 days, whichever is earlier.",
    atMeeting: true,
    cite: "NRS 608.020, 608.030",
    source: "statute",
    penalty: "Customary wages continue until paid, up to 30 days.",
  },
  NH: {
    discharge: "Within 72 hours.",
    quit: "By the next regular payday, or within 72 hours if the employee gave one pay period's notice.",
    layoff: "By the next regular payday.",
    atMeeting: false,
    cite: "RSA 275:44",
    source: "statute",
  },
  NJ: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "N.J.S.A. 34:11-4.3",
    source: "compilers",
  },
  NM: {
    discharge:
      "Within 5 days for a fixed, definite amount; within 10 days when the amount depends on tasks, pieces or commission.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "NMSA 50-4-4, 50-4-5",
    source: "agency",
    penalty: "Wages continue until paid, up to 60 days after discharge.",
  },
  NY: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "N.Y. Labor Law 191(3)",
    source: "compilers",
    note: "Accrued vacation is paid out unless a written policy forfeits it.",
  },
  NC: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "N.C.G.S. 95-25.7",
    source: "compilers",
  },
  ND: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "N.D.C.C. 34-14-03",
    source: "compilers",
  },
  OH: {
    discharge: "The earlier of the next regular payday and 15 days.",
    quit: "The earlier of the next regular payday and 15 days.",
    atMeeting: false,
    cite: "ORC 4113.15",
    source: "compilers",
  },
  OK: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "Okla. Stat. tit. 40 s.165.3",
    source: "compilers",
  },
  OR: {
    discharge: "By the end of the first business day after discharge.",
    quit: "Immediately if the employee gave at least 48 hours' notice; otherwise within 5 business days or by the next regular payday, whichever is first.",
    atMeeting: false,
    cite: "ORS 652.140",
    source: "statute",
  },
  PA: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "43 P.S. 260.5",
    source: "agency",
    penalty: "Liquidated damages of 25% of the wages due or $500, whichever is greater (260.10).",
  },
  RI: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "R.I. Gen. Laws 28-14-4",
    source: "compilers",
  },
  SC: {
    discharge: "Within 48 hours, or by the next regular payday if later, but within 30 days.",
    quit: "Within 48 hours, or by the next regular payday if later, but within 30 days.",
    atMeeting: false,
    cite: "S.C. Code 41-10-50",
    source: "compilers",
  },
  SD: {
    discharge: "By the next regular payday (the employer may wait for company property to be returned).",
    quit: "By the next regular payday (the employer may wait for company property to be returned).",
    atMeeting: false,
    cite: "SDCL 60-11-10",
    source: "compilers",
  },
  TN: {
    discharge: "The later of the next regular payday and 21 days.",
    quit: "The later of the next regular payday and 21 days.",
    atMeeting: false,
    cite: "Tenn. Code 50-2-103(g)",
    source: "compilers",
  },
  TX: {
    discharge: "Within 6 calendar days.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "Tex. Lab. Code 61.014",
    source: "compilers",
  },
  UT: {
    discharge: "Within 24 hours.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "Utah Code 34-28-5",
    source: "agency",
    penalty: "Wages continue until paid, up to 60 days.",
  },
  VT: {
    discharge: "Within 72 hours.",
    quit: "On the regular payday, or the following Friday if there is none.",
    atMeeting: false,
    cite: "21 V.S.A. 342",
    source: "statute",
  },
  VA: {
    discharge: "By the next regular payday.",
    quit: "By the next regular payday.",
    atMeeting: false,
    cite: "Va. Code 40.1-29(A)",
    source: "compilers",
  },
  WA: {
    discharge: "At the end of the established pay period.",
    quit: "At the end of the established pay period.",
    atMeeting: false,
    cite: "RCW 49.48.010",
    source: "statute",
  },
  WV: {
    discharge: "On or before the next regular payday.",
    quit: "On or before the next regular payday.",
    layoff: "By the next regular payday.",
    atMeeting: false,
    cite: "W. Va. Code 21-5-4",
    source: "statute",
    note: "The former 72-hour rule for discharge no longer applies; older summaries still print it.",
  },
  WI: {
    discharge: "By the next regular payday under the employer's established payroll schedule.",
    quit: "By the next regular payday under the employer's established payroll schedule.",
    atMeeting: false,
    cite: "Wis. Stat. 109.03(2)",
    source: "statute",
  },
  WY: {
    discharge: "No later than the employer's usual practice on regularly scheduled payroll dates.",
    quit: "No later than the employer's usual practice on regularly scheduled payroll dates.",
    atMeeting: false,
    cite: "W.S. 27-4-104",
    source: "statute",
    note: "Some sites print a 5-working-day rule; the statute does not say that.",
  },
};

/** The state names for a picker, in order. */
export const STATE_NAMES: Readonly<Record<string, string>> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

/** A state the table covers. Blank (unset) is allowed elsewhere; this is about a value that was given. */
export const isStateCode = (value: unknown): value is string =>
  typeof value === "string" && Object.hasOwn(FINAL_PAY, value);

/** Which of the rule's three cases a separation is. Layoff is its own where a state treats it so. */
export type SeparationKind = "discharge" | "quit" | "layoff";

/** Resignation and retirement are the employee's choice; everything else the employer's. */
export const separationKind = (trigger: string): SeparationKind =>
  trigger === "resignation" || trigger === "retirement" ? "quit" : "discharge";

/** What the law says for this state and separation, or null for a state we have no row for. */
export function finalPayText(state: string, trigger: string): string | null {
  const rule = FINAL_PAY[state];
  if (!rule) return null;
  const kind = separationKind(trigger);
  const deadline = kind === "quit" ? rule.quit : rule.discharge;
  const bits = [
    `${STATE_NAMES[state] ?? state}: ${deadline}`,
    rule.penalty,
    rule.note,
    rule.cite === "none" ? undefined : `(${rule.cite})`,
    "Guidance, not legal advice.",
  ].filter(Boolean);
  return bits.join(" ");
}

/**
 * Whether a departure in this state has to have the check ready when the
 * person is told: an involuntary separation where the law says pay on the day.
 */
export function finalPayAtMeeting(state: string, trigger: string): boolean {
  const rule = FINAL_PAY[state];
  return !!rule && separationKind(trigger) === "discharge" && rule.atMeeting;
}
